// frontend/src/pages/admin/AdminOrders.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ordersApi } from '../../api/orders';
import { financeApi } from '../../api/finance'; 
import { useAuth } from '../../context/AuthContext'; 
import helpers from '../../utils/helpers';
import { 
    FaSearch, FaFilter, FaBox, FaTruck, FaCheck, FaTimes, FaEye, 
    FaMapMarkerAlt, FaCreditCard, FaPrint, FaStickyNote, FaSpinner, FaSave, FaDownload, FaTrash,
    FaMoneyBillWave, FaUndo, FaExclamationTriangle, FaInfoCircle, FaImage
} from 'react-icons/fa';

const SUPER_ADMIN_EMAIL = "esekiegabriel@gmail.com";

// --- REFUND MODAL COMPONENT ---
const RefundModal = ({ order, onClose, onRefundSuccess }) => {
    const [refundAmount, setRefundAmount] = useState(order.total_amount);
    const [reason, setReason] = useState('Customer requested refund');
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(null);
    
    // Find the Flutterwave Transaction ID (tx_ref)
    const transactionId = order.payment_ref;
    const maxAmount = order.total_amount;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        
        const amount = parseFloat(refundAmount);
        if (amount <= 0 || amount > maxAmount) {
            setError(`Amount must be between 1 and ${maxAmount}.`);
            return;
        }
        if (!transactionId) {
            setError("Cannot refund: No Flutterwave Transaction ID found on this order.");
            return;
        }

        if (!window.confirm(`Confirm refund of NGN ${amount} for Order ${order.order_number}?`)) return;

        setProcessing(true);
        try {
            // Call the new refund API
            const response = await financeApi.initiateRefund(transactionId, amount, reason);
            
            if (response.status === 'success') {
                alert(`Refund of NGN ${amount} initiated successfully! Status: ${response.data.status}`);
                onRefundSuccess();
            } else {
                throw new Error(response.message || "Refund failed.");
            }
        } catch (err) {
            console.error("Refund Error:", err);
            setError(err.message || "Refund failed. Check Flutterwave logs.");
        } finally {
            setProcessing(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 animate-fadeInUp">
                <div className="flex justify-between items-center mb-4 border-b pb-2">
                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2"><FaUndo /> Initiate Refund</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
                </div>
                
                {order.payment_method !== 'online' ? (
                    <div className="bg-red-100 text-red-700 p-4 rounded flex items-center gap-2">
                        <FaExclamationTriangle />
                        <p className="text-sm font-bold">Cannot Refund: Payment method was {order.payment_method.replace('_', ' ')}. Manual refund required.</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="bg-blue-50 p-3 rounded text-xs text-blue-800 flex items-start gap-2">
                            <FaInfoCircle className="mt-0.5 flex-shrink-0" />
                            <p>Refunding NGN {maxAmount} will be a **Full Refund**. Any amount less will be a **Partial Refund**.</p>
                        </div>

                        {error && <div className="bg-red-100 text-red-700 p-3 rounded text-sm">{error}</div>}

                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">Order Total</label>
                            <input type="text" value={helpers.formatCurrency(maxAmount, 'NGN')} disabled className="w-full p-2 border rounded bg-gray-100 font-bold text-lg" />
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">Refund Amount (NGN)</label>
                            <input 
                                type="number" 
                                step="0.01"
                                value={refundAmount}
                                onChange={e => setRefundAmount(e.target.value)}
                                max={maxAmount}
                                min="1"
                                required
                                className="w-full p-2 border rounded focus:ring-2 focus:ring-primary outline-none"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">Reason</label>
                            <input 
                                type="text" 
                                value={reason}
                                onChange={e => setReason(e.target.value)}
                                required
                                className="w-full p-2 border rounded focus:ring-2 focus:ring-primary outline-none"
                            />
                        </div>

                        <div className="flex justify-end gap-3 pt-2 border-t">
                            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-600">Cancel</button>
                            <button type="submit" disabled={processing} className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-2">
                                {processing ? <FaSpinner className="animate-spin" /> : <FaMoneyBillWave />} Confirm Refund
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};
// --- END REFUND MODAL COMPONENT ---


function AdminOrders() {
  const { user } = useAuth(); 
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // --- FILTERS ---
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPayment, setFilterPayment] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [searchTerm, setSearchTerm] = useState('');
  
  // --- BULK ACTIONS ---
  const [selectedIds, setSelectedIds] = useState([]);
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false); 

  // --- MODALS & UPDATES ---
  const [updatingId, setUpdatingId] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null); 
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  
  // Note Modal
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [noteOrder, setNoteOrder] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  
  // Refund Modal
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [refundTarget, setRefundTarget] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    filterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, filterStatus, filterPayment, dateRange, searchTerm]);

  const fetchOrders = async () => {
    try {
      const data = await ordersApi.getAllOrdersAdmin();
      setOrders(data);
    } catch (err) {
      console.error("Error fetching orders:", err);
      alert("Failed to load orders.");
    } finally {
      setLoading(false);
    }
  };

  const filterData = () => {
    let result = orders;

    if (filterStatus !== 'all') {
      result = result.filter(o => o.status === filterStatus);
    }

    if (filterPayment !== 'all') {
        result = result.filter(o => o.payment_method === filterPayment);
    }

    if (dateRange.start) {
        result = result.filter(o => new Date(o.created_at) >= new Date(dateRange.start));
    }
    if (dateRange.end) {
        const endDate = new Date(dateRange.end);
        endDate.setDate(endDate.getDate() + 1); 
        result = result.filter(o => new Date(o.created_at) < endDate);
    }

    if (searchTerm) {
      const lowerTerm = searchTerm.toLowerCase();
      result = result.filter(o => 
        o.order_number.toLowerCase().includes(lowerTerm) ||
        (o.shipping_address?.email && o.shipping_address.email.toLowerCase().includes(lowerTerm)) ||
        (o.shipping_address?.full_name && o.shipping_address.full_name.toLowerCase().includes(lowerTerm))
      );
    }

    setFilteredOrders(result);
  };

  // --- BULK HANDLERS ---
  const handleSelectAll = (e) => {
      if (e.target.checked) {
          setSelectedIds(filteredOrders.map(o => o.id));
      } else {
          setSelectedIds([]);
      }
  };

  const handleSelectOne = (id) => {
      setSelectedIds(prev => 
          prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
      );
  };

  const handleBulkStatusUpdate = async (newStatus) => {
      if (selectedIds.length === 0) return;
      if (!window.confirm(`Mark ${selectedIds.length} orders as ${newStatus.toUpperCase()}?`)) return;

      setIsBulkProcessing(true);
      try {
          await ordersApi.bulkUpdateStatus(selectedIds, newStatus);
          alert("Bulk update successful!");
          setSelectedIds([]);
          fetchOrders();
      } catch (err) {
          alert("Failed to update orders.");
      } finally {
          setIsBulkProcessing(false);
      }
  };

  // --- SUPER ADMIN DELETE ---
  const handleBulkDelete = async () => {
      if (selectedIds.length === 0) return;
      if (!window.confirm(`⚠️ PERMANENTLY DELETE ${selectedIds.length} orders? This cannot be undone.`)) return;

      setIsDeleting(true);
      try {
          await ordersApi.deleteOrdersAdmin(selectedIds);
          alert("Orders deleted successfully.");
          setSelectedIds([]);
          fetchOrders();
      } catch (err) {
          alert("Failed to delete orders. Ensure you are the Super Admin.");
      } finally {
          setIsDeleting(false);
      }
  };

  // --- SINGLE ACTIONS ---
  const handleStatusUpdate = async (id, newStatus) => {
    let trackingNumber = null;
    if (newStatus === 'shipped') {
      trackingNumber = prompt("Enter Tracking Number (Optional - Leave blank for auto-generate):");
    } else {
      if (!window.confirm(`Change status to ${newStatus.toUpperCase()}?`)) return;
    }

    setUpdatingId(id);
    try {
      await ordersApi.updateOrderStatus(id, newStatus, trackingNumber);
      setOrders(prev => prev.map(o => 
        o.id === id ? { ...o, status: newStatus, tracking_number: trackingNumber || o.tracking_number } : o
      ));
      if (selectedOrder && selectedOrder.id === id) {
          setSelectedOrder(prev => ({ ...prev, status: newStatus }));
      }
    } catch (err) {
      alert("Failed to update order.");
    } finally {
      setUpdatingId(null);
    }
  };

  const openNoteModal = (order) => {
      setNoteOrder(order);
      setNoteText(order.internal_notes || '');
      setNoteModalOpen(true);
  };

  const handleSaveNote = async () => {
      setSavingNote(true);
      try {
          await ordersApi.updateOrderNote(noteOrder.id, noteText);
          setOrders(prev => prev.map(o => o.id === noteOrder.id ? { ...o, internal_notes: noteText } : o));
          setNoteModalOpen(false);
      } catch (err) {
          alert("Failed to save note.");
      } finally {
          setSavingNote(false);
      }
  };

  const handleDownloadPdf = async () => {
      if (!selectedOrder) return;
      setDownloadingPdf(true);
      try {
          await ordersApi.downloadInvoice(selectedOrder.id);
      } catch (err) {
          alert(err.message || "Failed to download PDF.");
      } finally {
          setDownloadingPdf(false);
      }
  };
  
  // --- REFUND HANDLERS ---
  const openRefundModal = (order) => {
      setRefundTarget(order);
      setIsRefundModalOpen(true);
  };

  const handleRefundSuccess = () => {
      setIsRefundModalOpen(false);
      // Optionally update the order status to 'refunded' in the DB
      // For now, we just close the modal and let the user see the refund status in Flutterwave
  };
  // ------------------------

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'shipped': return 'bg-purple-100 text-purple-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) return <div className="p-8 text-center"><FaSpinner className="animate-spin text-2xl text-primary" /> Loading orders...</div>;

  return (
    <div className="pb-20">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Order Management</h1>

      {/* --- FILTERS BAR --- */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-6 flex flex-col gap-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative col-span-1 sm:col-span-2 lg:col-span-1">
                <input 
                    type="text" 
                    placeholder="Search Order #, Email..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-primary"
                />
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>

            <div className="flex items-center gap-2 border border-gray-300 rounded px-3 py-2 bg-white">
                <FaFilter className="text-gray-400" />
                <select 
                    value={filterStatus} 
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="w-full focus:outline-none bg-transparent"
                >
                    <option value="all">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                </select>
            </div>

            <select 
                value={filterPayment} 
                onChange={(e) => setFilterPayment(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-primary bg-white"
            >
                <option value="all">All Payment Methods</option>
                <option value="online">Online Payment</option>
                <option value="pay_on_delivery">Pay on Delivery</option>
            </select>

            <div className="flex gap-2">
                <input 
                    type="date" 
                    value={dateRange.start}
                    onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                    className="w-1/2 border border-gray-300 rounded px-2 py-2 text-sm"
                />
                <input 
                    type="date" 
                    value={dateRange.end}
                    onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                    className="w-1/2 border border-gray-300 rounded px-2 py-2 text-sm"
                />
            </div>
        </div>
      </div>

      {/* --- BULK ACTIONS BAR --- */}
      {selectedIds.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg mb-4 flex flex-wrap items-center justify-between gap-4 animate-fadeIn">
              <span className="font-bold text-blue-800 text-sm">{selectedIds.length} orders selected</span>
              <div className="flex gap-2">
                  <button 
                      onClick={() => handleBulkStatusUpdate('processing')}
                      disabled={isBulkProcessing}
                      className="bg-blue-600 text-white px-3 py-1 rounded text-xs font-bold hover:bg-blue-700 flex items-center gap-1"
                  >
                      {isBulkProcessing && <FaSpinner className="animate-spin" />} Mark Processing
                  </button>
                  <button 
                      onClick={() => handleBulkStatusUpdate('shipped')}
                      disabled={isBulkProcessing}
                      className="bg-purple-600 text-white px-3 py-1 rounded text-xs font-bold hover:bg-purple-700 flex items-center gap-1"
                  >
                      {isBulkProcessing && <FaSpinner className="animate-spin" />} Mark Shipped
                  </button>
                  <button 
                      onClick={() => handleBulkStatusUpdate('delivered')}
                      disabled={isBulkProcessing}
                      className="bg-green-600 text-white px-3 py-1 rounded text-xs font-bold hover:bg-green-700 flex items-center gap-1"
                  >
                      {isBulkProcessing && <FaSpinner className="animate-spin" />} Mark Delivered
                  </button>

                  {/* --- SUPER ADMIN DELETE BUTTON --- */}
                  {user?.email.toLowerCase() === SUPER_ADMIN_EMAIL.toLowerCase() && (
                      <button 
                          onClick={handleBulkDelete}
                          disabled={isDeleting}
                          className="bg-red-600 text-white px-3 py-1 rounded text-xs font-bold hover:bg-red-700 flex items-center gap-1 ml-2"
                      >
                          {isDeleting ? <FaSpinner className="animate-spin" /> : <FaTrash />} Delete
                      </button>
                  )}
              </div>
          </div>
      )}

      {/* --- TABLE --- */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[1100px]">
            <thead>
              <tr className="bg-gray-50 text-gray-600 text-sm uppercase tracking-wider border-b border-gray-200">
                <th className="p-4 w-10">
                    <input 
                        type="checkbox" 
                        onChange={handleSelectAll}
                        checked={filteredOrders.length > 0 && selectedIds.length === filteredOrders.length}
                        className="h-4 w-4 text-primary rounded border-gray-300 focus:ring-primary cursor-pointer"
                    />
                </th>
                <th className="p-4 w-16"><FaImage /></th>
                <th className="p-4">Order #</th>
                <th className="p-4">Date</th>
                <th className="p-4">Customer</th>
                <th className="p-4">Total</th>
                <th className="p-4">Payment</th>
                <th className="p-4">Status</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan="9" className="p-8 text-center text-gray-500">No orders found.</td>
                </tr>
              ) : (
                filteredOrders.map((order) => {
                    const firstItem = order.items?.[0] || {};
                    const itemImage = firstItem.image || 'https://via.placeholder.com/50';
                    
                    return (
                      <tr key={order.id} className={`hover:bg-gray-50 transition-colors ${selectedIds.includes(order.id) ? 'bg-blue-50' : ''}`}>
                        <td className="p-4">
                            <input 
                                type="checkbox" 
                                checked={selectedIds.includes(order.id)}
                                onChange={() => handleSelectOne(order.id)}
                                className="h-4 w-4 text-primary rounded border-gray-300 focus:ring-primary cursor-pointer"
                            />
                        </td>
                        {/* --- IMAGE CELL --- */}
                        <td className="p-2">
                            <div className="w-10 h-10 bg-gray-100 rounded overflow-hidden border border-gray-200">
                                <img src={itemImage} alt="Item" className="w-full h-full object-cover" />
                            </div>
                        </td>
                        <td className="p-4">
                          <span 
                            className="font-mono text-sm font-bold text-primary cursor-pointer hover:underline block" 
                            onClick={() => setSelectedOrder(order)}
                          >
                            {order.order_number}
                          </span>
                          {order.internal_notes && (
                            <button 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openNoteModal(order);
                                }}
                                className="mt-1 text-yellow-500 hover:text-yellow-600 flex items-center gap-1 text-xs font-bold"
                                title="View/Edit Note"
                            >
                                <FaStickyNote /> Note
                            </button>
                          )}
                        </td>
                        <td className="p-4 text-sm text-gray-600">
                          {new Date(order.created_at).toLocaleDateString()}
                        </td>
                        <td className="p-4 text-sm">
                          <div className="font-medium text-gray-800">{order.shipping_address?.full_name || 'Unknown'}</div>
                          <div className="text-xs text-gray-500">{order.shipping_address?.email}</div>
                        </td>
                        <td className="p-4 text-sm font-bold text-gray-800">
                          {helpers.formatCurrency(order.total_amount, 'NGN')}
                        </td>
                        <td className="p-4 text-xs capitalize text-gray-600">
                            {order.payment_method ? order.payment_method.replace('_', ' ') : 'N/A'}
                        </td>
                        <td className="p-4">
                          <span className={`inline-block px-2 py-1 rounded text-xs font-bold uppercase ${getStatusColor(order.status)}`}>
                            {order.status}
                          </span>
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex justify-end gap-2">
                            
                            {/* REFUND BUTTON (NEW) */}
                            {order.payment_method === 'online' && order.status !== 'cancelled' && (
                                <button 
                                    onClick={() => openRefundModal(order)}
                                    className="p-2 text-red-600 hover:bg-red-100 rounded"
                                    title="Initiate Refund"
                                >
                                    <FaUndo />
                                </button>
                            )}

                            <button 
                                onClick={() => openNoteModal(order)} 
                                className={`p-2 rounded transition-colors ${order.internal_notes ? 'text-yellow-500 hover:bg-yellow-50' : 'text-gray-400 hover:text-yellow-600 hover:bg-gray-100'}`}
                                title={order.internal_notes ? "Edit Note" : "Add Note"}
                            >
                                <FaStickyNote />
                            </button>

                            <button onClick={() => setSelectedOrder(order)} className="text-gray-400 hover:text-blue-600 p-2" title="View Details">
                                <FaEye />
                            </button>
                            
                            {order.status === 'pending' && (
                                <>
                                    <button 
                                        onClick={() => handleStatusUpdate(order.id, 'processing')}
                                        disabled={updatingId === order.id}
                                        className="text-blue-400 hover:text-blue-600 p-2" 
                                        title="Process"
                                    >
                                        {updatingId === order.id ? <FaSpinner className="animate-spin" /> : <FaBox />}
                                    </button>
                                    <button 
                                        onClick={() => handleStatusUpdate(order.id, 'cancelled')}
                                        disabled={updatingId === order.id}
                                        className="text-red-400 hover:text-red-600 p-2" 
                                        title="Cancel"
                                    >
                                        {updatingId === order.id ? <FaSpinner className="animate-spin" /> : <FaTimes />}
                                    </button>
                                </>
                            )}
                            {order.status === 'processing' && (
                                <button 
                                    onClick={() => handleStatusUpdate(order.id, 'shipped')}
                                    disabled={updatingId === order.id}
                                    className="text-purple-400 hover:text-purple-600 p-2" 
                                    title="Ship"
                                >
                                    {updatingId === order.id ? <FaSpinner className="animate-spin" /> : <FaTruck />}
                                </button>
                            )}
                            {order.status === 'shipped' && (
                                <button 
                                    onClick={() => handleStatusUpdate(order.id, 'delivered')}
                                    disabled={updatingId === order.id}
                                    className="text-green-400 hover:text-green-600 p-2" 
                                    title="Mark Delivered"
                                >
                                    {updatingId === order.id ? <FaSpinner className="animate-spin" /> : <FaCheck />}
                                </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- ORDER DETAILS MODAL (PRINTABLE) --- */}
      {selectedOrder && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-75 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col animate-fadeInUp">
            
            <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-gray-50 no-print">
              <div>
                <h2 className="text-xl font-bold text-gray-800">Order Details</h2>
                <p className="text-sm text-gray-500 font-mono">{selectedOrder.order_number}</p>
              </div>
              <div className="flex gap-3">
                  <button 
                    onClick={handleDownloadPdf} 
                    disabled={downloadingPdf}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-bold flex items-center gap-2"
                  >
                      {downloadingPdf ? <FaSpinner className="animate-spin" /> : <FaDownload />} Download PDF
                  </button>
                  <button 
                    onClick={() => window.print()} 
                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded font-bold flex items-center gap-2"
                  >
                      <FaPrint /> Print
                  </button>
                  <button onClick={() => setSelectedOrder(null)} className="text-gray-400 hover:text-red-500">
                    <FaTimes size={24} />
                  </button>
              </div>
            </div>

            <div className="p-8 overflow-y-auto printable-content">
              <div className="flex justify-between items-start mb-8 border-b pb-4">
                  <div>
                      <h1 className="text-2xl font-bold text-primary">Everything Automotive</h1>
                      <p className="text-sm text-gray-500">5 Adejuwon Street, Ikotun, Lagos</p>
                      <p className="text-sm text-gray-500">support@everything-automotive.com</p>
                  </div>
                  <div className="text-right">
                      <h2 className="text-xl font-bold text-gray-800">INVOICE</h2>
                      <p className="text-sm text-gray-600">Order #: {selectedOrder.order_number}</p>
                      <p className="text-sm text-gray-600">Date: {new Date(selectedOrder.created_at).toLocaleDateString()}</p>
                  </div>
              </div>

              <div className="grid grid-cols-2 gap-8 mb-8">
                  <div>
                      <h3 className="text-xs font-bold text-gray-500 uppercase mb-2">Bill To:</h3>
                      <p className="font-bold text-gray-800">{selectedOrder.shipping_address.full_name}</p>
                      <p className="text-sm text-gray-600">{selectedOrder.shipping_address.email}</p>
                      <p className="text-sm text-gray-600">{selectedOrder.shipping_address.phone}</p>
                  </div>
                  <div>
                      <h3 className="text-xs font-bold text-gray-500 uppercase mb-2 flex items-center gap-1">
                          <FaMapMarkerAlt /> Ship To:
                      </h3>
                      <p className="text-sm text-gray-600">{selectedOrder.shipping_address.address_line1}</p>
                      <p className="text-sm text-gray-600">{selectedOrder.shipping_address.city}, {selectedOrder.shipping_address.state}</p>
                  </div>
              </div>

              <div className="mb-6">
                  <h3 className="text-xs font-bold text-gray-500 uppercase mb-2 flex items-center gap-1">
                      <FaCreditCard /> Payment Method:
                  </h3>
                  <p className="text-sm font-medium text-gray-800 capitalize">
                      {selectedOrder.payment_method ? selectedOrder.payment_method.replace('_', ' ') : 'N/A'}
                  </p>
              </div>

              <table className="w-full text-left mb-8 border-collapse">
                  <thead>
                      <tr className="border-b-2 border-gray-800">
                          <th className="py-2 text-sm font-bold text-gray-700">Item</th>
                          <th className="py-2 text-center text-sm font-bold text-gray-700">Qty</th>
                          <th className="py-2 text-right text-sm font-bold text-gray-700">Price</th>
                          <th className="py-2 text-right text-sm font-bold text-gray-700">Total</th>
                      </tr>
                  </thead>
                  <tbody>
                      {selectedOrder.items.map((item, idx) => (
                          <tr key={idx} className="border-b border-gray-200">
                              <td className="py-3 text-sm text-gray-800">{item.name}</td>
                              <td className="py-3 text-center text-sm text-gray-600">{item.quantity}</td>
                              <td className="py-3 text-right text-sm text-gray-600">{helpers.formatCurrency(item.price, 'NGN')}</td>
                              <td className="py-3 text-right text-sm font-bold text-gray-800">{helpers.formatCurrency(item.price * item.quantity, 'NGN')}</td>
                          </tr>
                      ))}
                  </tbody>
                  <tfoot>
                      <tr>
                          <td colSpan="3" className="py-4 text-right font-bold text-gray-700">Grand Total:</td>
                          <td className="py-4 text-right font-bold text-xl text-primary">{helpers.formatCurrency(selectedOrder.total_amount, 'NGN')}</td>
                      </tr>
                  </tfoot>
              </table>

              <div className="border-t pt-4 text-center text-xs text-gray-500">
                  <p>Payment Method: <span className="capitalize font-bold">{selectedOrder.payment_method ? selectedOrder.payment_method.replace('_', ' ') : 'N/A'}</span></p>
                  <p className="mt-1">Thank you for your business!</p>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* --- INTERNAL NOTE MODAL --- */}
      {noteModalOpen && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 animate-fadeInUp">
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Internal Note</h3>
                  <p className="text-sm text-gray-600 mb-4">
                      Add private notes for Order #{noteOrder.order_number}. These are NOT visible to the customer.
                  </p>
                  <textarea 
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      className="w-full p-3 border rounded focus:ring-2 focus:ring-yellow-400 outline-none bg-yellow-50"
                      rows="5"
                      placeholder="e.g. Customer called, wants delivery after 5 PM..."
                  ></textarea>
                  <div className="flex justify-end gap-2 mt-4">
                      <button onClick={() => setNoteModalOpen(false)} className="px-4 py-2 text-gray-600">Cancel</button>
                      <button 
                          onClick={handleSaveNote} 
                          disabled={savingNote}
                          className="bg-yellow-500 text-white px-4 py-2 rounded font-bold hover:bg-yellow-600 flex items-center gap-2"
                      >
                          {savingNote ? <FaSpinner className="animate-spin" /> : <FaSave />} Save Note
                      </button>
                  </div>
              </div>
          </div>
      )}
      
      {/* --- REFUND MODAL --- */}
      {isRefundModalOpen && refundTarget && (
          <RefundModal 
              order={refundTarget}
              onClose={() => setIsRefundModalOpen(false)}
              onRefundSuccess={fetchOrders}
          />
      )}

    </div>
  );
}

export default AdminOrders;